import { globalStores, TGlobalStores } from '.';
import { ESeparateWallType, SeparateWallBaseData, SeparateWallData } from '../data/separateWall/SeparateWall';
import { createSeparateWallData, getSeparateWallOutline, initDrawSeparateWallInfo } from '../data/separateWall/utils';
import { Curve2dUtils } from '../math/Curve2dUtils';
import { IPoint2d } from '../math/IPoint2d';
import { LineSeg2d } from '../math/LineSeg2d';
import { Point2dUtils } from '../math/Point2dUtils';
import { ISimpleUniformShapeWallInfo, IWallShape } from '../types/wallShape';
import { getBaseShape } from '../utils/wallShape';
import RoomStore from './RoomStore';
import { BaseSepStore } from './base/baseSepStore';
import { getRelativeWallIdsThatNeedUpdateShape } from './utils';
import { DEFAULT_SEP_WALL_THICKNESS } from '../constant/wall';
import { getPathFromEdges } from '../utils/edge';
import { IUniformWallData } from '../types';
import { IWallAbsorbUniformStoreOptions } from '../types/absorb';
import { SeparateWallShapeCalculate } from '../utils/separateWallShape/separateWallShapeCalculate';

export type TSepWallInRangeMode = 'intersect' | 'contain';

class SeparateWallStore {
  private _baseSepWallMap: BaseSepStore; // 基础隔墙数据
  private _separateWallMap: BaseSepStore; // 非基础隔墙数据

  private _shapeCalculate: SeparateWallShapeCalculate;
  private _roomStore: RoomStore;

  constructor(gs: TGlobalStores) {
    this._baseSepWallMap = new BaseSepStore(gs);
    this._separateWallMap = new BaseSepStore(gs);

    this._roomStore = gs.roomStore;
    this._shapeCalculate = new SeparateWallShapeCalculate(this);
  }

  get baseSeparateWallDatas() {
    return this._baseSepWallMap.getList();
  }

  get separateWallDatas() {
    return this._separateWallMap.getList();
  }

  private getSepDataMapById(separateWallId: string) {
    if (this._baseSepWallMap.get(separateWallId)) return this._baseSepWallMap;
    if (this._separateWallMap.get(separateWallId)) return this._separateWallMap;

    return null;
  }

  getSepData(separateWallId: string, isBase: boolean) {
    if (isBase) {
      return this._baseSepWallMap.get(separateWallId);
    }

    return this._separateWallMap.get(separateWallId);
  }

  getSepDatas(options?: { onlyBase?: boolean; onlyNotBase?: boolean; roomIds?: string[] }) {
    const { onlyBase, onlyNotBase, roomIds } = options ?? {};

    if (onlyBase) {
      const baseSepDatas = this._baseSepWallMap.getList();
      if (!roomIds) return baseSepDatas;
      return baseSepDatas.filter((swd) => roomIds.includes(swd.relateRoomId));
    }

    if (onlyNotBase) {
      const separateSepDatas = this._separateWallMap.getList();
      if (!roomIds) return separateSepDatas;
      return separateSepDatas.filter((swd) => roomIds.includes(swd.relateRoomId));
    }

    const allSepDatas = [...this._baseSepWallMap.getList(), ...this._separateWallMap.getList()];

    if (!roomIds) return allSepDatas;
    return allSepDatas.filter((swd) => roomIds.includes(swd.relateRoomId));
  }

  getUniformWallData(id: string): IUniformWallData | undefined {
    let sepWallData: SeparateWallData | undefined;
    if (this._baseSepWallMap.get(id)) {
      sepWallData = this._baseSepWallMap.get(id);
    } else {
      sepWallData = this._separateWallMap.get(id);
    }

    if (!sepWallData) return;

    return {
      id: sepWallData.id,
      start: sepWallData.start,
      end: sepWallData.end,
      thickness: sepWallData.thickness,
      center: sepWallData.center,
      clockwise: sepWallData.clockwise
    };
  }

  getUniformShapeWallInfos(options?: IWallAbsorbUniformStoreOptions): ISimpleUniformShapeWallInfo[] {
    return this.getSepDatas({ roomIds: options?.roomIds }).map((item) => {
      const uniformWallData: ISimpleUniformShapeWallInfo = {
        id: item.id,
        roomId: item.relateRoomId,
        start: item.start,
        end: item.end,
        thickness: item.thickness,
        startShape: item.drawSeparateWallInfo.startShape,
        endShape: item.drawSeparateWallInfo.endShape,
        drawShape: item.drawSeparateWallInfo.drawShape,
        skipCalculate: item.type === ESeparateWallType.VirtualWall
      };

      if (item.center) {
        uniformWallData.center = item.center;
        uniformWallData.clockwise = item.clockwise || false;
      }

      return uniformWallData;
    });
  }

  /**
   * 获取多边形区域内的隔墙列表（只查绘制的隔墙，不含房间轮廓的基础隔墙）
   *
   * @param polygon 多边形区域
   * @param mode 判断模式，`contain` 表示隔墙轮廓完全被多边形包含，`intersect` 表示隔墙轮廓与多边形相交
   * @returns 隔墙列表
   */
  getSepDatasInPolygon(polygon: IPoint2d[], mode: TSepWallInRangeMode = 'contain') {
    if (polygon.length < 3) return [];

    return this.getSepDatas({ onlyNotBase: true }).filter((sepWallData) => {
      // 虚拟隔墙厚度为 0，拿到的是只有两个点的中心线，closed 为 false
      const { points: outline, closed } = getSeparateWallOutline(sepWallData);
      if (outline.length < 2) return false;

      return mode === 'contain'
        ? isOutlineInPolygon(outline, polygon)
        : isOutlineIntersectPolygon(outline, polygon, closed);
    });
  }

  getConnectedWallInfo(separateWallId: string, skipVirtualWall: boolean = true) {
    return this._shapeCalculate.updateWallInfos(skipVirtualWall).getConnectedWallInfo(separateWallId);
  }

  getConnectedWallInfos(skipVirtualWall: boolean = true) {
    return this._shapeCalculate.updateWallInfos(skipVirtualWall).getConnectedWallInfos();
  }

  private addSepBaseData(data: SeparateWallData, isBase: boolean) {
    if (isBase) {
      this._baseSepWallMap.add(data);
    } else {
      this._separateWallMap.add(data);
    }
  }

  addSepData(data: SeparateWallData, isBase: boolean) {
    this.addSepBaseData(data, isBase);
    if (!isBase) {
      // 新增隔墙时，除了自身，还要重算与它端点相连的其它隔墙——否则先画的隔墙在与新墙相连的那一端
      // 保留旧端盖(绘制时显示错误，保存重开全量算才正确)。与 updateSepBaseData/removeSepData 保持一致。
      const connectWallInfos = this._shapeCalculate.updateWallInfos().getConnectedWallInfo(data.id);
      this.computeThenUpdateWallsShape(getRelativeWallIdsThatNeedUpdateShape(data.id, connectWallInfos));
    }
  }

  updateSepBaseData(id: string, data: Partial<SeparateWallBaseData>, isBase: boolean) {
    const dataMap = isBase ? this._baseSepWallMap : this._separateWallMap;

    // 缓存旧关联墙体
    const prevConnectWallInfos = this._shapeCalculate.updateWallInfos().getConnectedWallInfo(id);
    dataMap.updateBaseData(id, data);

    // 更新隔墙基础轮廓
    const newData = dataMap.get(id);
    if (!newData) return;
    const { startShape, endShape, baseShape } = getBaseShape(newData);
    const drawSeparateWallInfo = { startShape, endShape, drawShape: baseShape };
    dataMap.updateExtraData(id, { drawSeparateWallInfo });

    // 更新所有关联墙体轮廓
    this.computeThenUpdateWallsShape(getRelativeWallIdsThatNeedUpdateShape(id, prevConnectWallInfos));
  }

  updateWallShape(separateWallId: string, drawInfo: IWallShape) {
    this.getSepDataMapById(separateWallId)?.updateExtraData(separateWallId, { drawSeparateWallInfo: drawInfo });
  }

  removeSepData(id: string, isBase: boolean) {
    const dataMap = isBase ? this._baseSepWallMap : this._separateWallMap;
    const prevConnectWallInfos = this._shapeCalculate.updateWallInfos().getConnectedWallInfo(id);
    dataMap.remove(id);

    // 被删墙的邻墙端盖里含有被删墙的斜接/裁切点，而计算是以各墙当前 drawShape 为输入的，
    // 不复位就会把已删墙的几何再算一遍(端盖保持不变)。所以重算前先把它们退回基础矩形轮廓。
    const updateShapeIds = getRelativeWallIdsThatNeedUpdateShape(id, prevConnectWallInfos);
    this.resetWallsShapeToBase(updateShapeIds);
    this.computeThenUpdateWallsShape(updateShapeIds);
  }

  // 将墙体轮廓复位成不含任何端盖计算结果的基础矩形轮廓
  private resetWallsShapeToBase(wallIds: Set<string> | string[]) {
    wallIds.forEach((wallId) => {
      const dataMap = this.getSepDataMapById(wallId);
      const wallData = dataMap?.get(wallId);
      if (!dataMap || !wallData) return;

      const { startShape, endShape, baseShape } = getBaseShape(wallData);
      dataMap.updateExtraData(wallId, { drawSeparateWallInfo: { startShape, endShape, drawShape: baseShape } });
    });
  }

  // 通过房间ID和相对墙体ID获取基础隔墙数据
  private getBaseSeparateWallData(starPointId: number, endPointId: number) {
    return this.baseSeparateWallDatas.find((bswd) => {
      return bswd.startId === starPointId && bswd.endId === endPointId;
    });
  }

  getSeparateWallDataFromStartEnd(start: IPoint2d, end: IPoint2d) {
    const separateWallData = this.getSepDatas({ onlyNotBase: true }).find(
      (bswd) => Point2dUtils.equals(start, bswd.start) && Point2dUtils.equals(end, bswd.end)
    );

    return separateWallData;
  }

  // 根据房间内轮廓更新基础隔墙数据
  refreshBaseSeparateWallDatas() {
    const roomDatas = this._roomStore.getRoomDatas();
    // const newBaseSeparateWallDatas: SeparateWallData[] = [];
    const removeBaseSepWallIds = new Set<string>(this._baseSepWallMap.getList().map((data) => data.id));

    for (const roomData of roomDatas) {
      const floorPaths = [
        getPathFromEdges(
          roomData.pathInfos.map((info) => ({
            from: info.start,
            to: info.end,
            center: info.center,
            clockwise: info.clockwise
          }))
        ),
        ...roomData.holeInfos.map((holes) =>
          getPathFromEdges(
            holes.map((hole) => ({ from: hole.start, to: hole.end, center: hole.center, clockwise: hole.clockwise }))
          )
        )
      ];

      const floorPathInfos = [roomData.pathInfos, ...roomData.holeInfos];
      for (let i = 0; i < floorPaths.length; i++) {
        const outline = floorPaths[i];
        const outlineInfos = floorPathInfos[i];

        for (let i = 0; i < outline.length; i++) {
          const start = { x: outline[i].x, y: outline[i].y };
          const end = { x: outline[(i + 1) % outline.length].x, y: outline[(i + 1) % outline.length].y };
          const center = outlineInfos[i].center;
          const clockwise = outlineInfos[i].clockwise;
          const startId = outlineInfos[i].startId;
          const endId = outlineInfos[i].endId;
          const baseSeparateWallData = this.getBaseSeparateWallData(startId, endId);
          const wallData = globalStores.wallStore.getWallData(outlineInfos[i].wallId);

          if (baseSeparateWallData) {
            // 更新基础隔墙数据
            const baseData: Partial<SeparateWallBaseData> = {};
            if (!Point2dUtils.equals(start, baseSeparateWallData.start)) {
              baseData.start = start;
            }
            if (!Point2dUtils.equals(end, baseSeparateWallData.end)) {
              baseData.end = end;
            }
            // 关联墙取不到时不动高度与离地，保留隔墙现有值
            if (wallData) {
              if (wallData.baseData.height !== baseSeparateWallData.height) {
                baseData.height = wallData.baseData.height;
              }
              if (wallData.baseData.offGround !== baseSeparateWallData.offGround) {
                baseData.offGround = wallData.baseData.offGround;
              }
            }
            if (roomData.id !== baseSeparateWallData.relateRoomId) {
              baseData.relateRoomId = roomData.id;
            }
            if (center !== baseSeparateWallData.center) {
              baseData.center = center;
            }
            if (clockwise !== baseSeparateWallData.clockwise) {
              baseData.clockwise = clockwise;
            }

            if (Object.keys(baseData).length > 0) {
              this.updateSepBaseData(baseSeparateWallData.id, baseData, true);
            } else {
              // 更新轮廓，历史数据恢复时，基础数据无法判断出是否有更改，需要重新计算轮廓
              const { startShape, endShape, baseShape } = getBaseShape(baseSeparateWallData);
              const drawSeparateWallInfo = { startShape, endShape, drawShape: baseShape };
              this._baseSepWallMap.updateExtraData(baseSeparateWallData.id, { drawSeparateWallInfo });
            }

            removeBaseSepWallIds.delete(baseSeparateWallData.id);
          } else {
            // 创建基础隔墙数据
            const baseSeparateWallData: SeparateWallData = createSeparateWallData({
              roomId: roomData.id,
              start: start,
              end: end,
              thickness: 0,
              height: wallData?.baseData.height ?? 0,
              offGround: wallData?.baseData.offGround ?? 0,
              type: ESeparateWallType.BaseVirtualWall,
              relativeWallId: outlineInfos[i].wallId,
              relativeWallSide: outlineInfos[i].side,
              startId: startId,
              endId: endId,
              side: outlineInfos[i].side,
              center: center,
              clockwise: clockwise
            });
            this.addSepData(baseSeparateWallData, true);
          }
        }
      }
    }

    removeBaseSepWallIds.forEach((id) => this._baseSepWallMap.remove(id));
  }

  private computeThenUpdateWallsShape(wallIds: string[] | Set<string>) {
    if (Array.isArray(wallIds)) wallIds = new Set(wallIds);
    if (!wallIds.size) return;

    // 更新绘制信息
    this._shapeCalculate.updateWallInfos().setWalls(wallIds).compute().updateShapes();
  }

  updateSeparateWallDatas(updateDatas: { id: string; data: Partial<SeparateWallBaseData> }[]) {
    updateDatas.forEach((updateData) => {
      const currentSeparateWallData = this.getSepData(updateData.id, false);
      if (!currentSeparateWallData) return;

      // 处理虚拟隔墙/非虚拟隔墙切换
      const isTypeChange = updateData.data.type && currentSeparateWallData.type !== updateData.data.type;
      if (isTypeChange && updateData.data.type === ESeparateWallType.VirtualWall) {
        // 非虚拟隔墙切到虚拟隔墙，厚度设为0
        updateData.data.thickness = 0;
      } else if (isTypeChange && currentSeparateWallData.type === ESeparateWallType.VirtualWall) {
        // 切到虚拟切非虚拟隔墙，厚度取全局参数
        updateData.data.thickness = DEFAULT_SEP_WALL_THICKNESS;
      }

      this.updateSepBaseData(updateData.id, updateData.data, false); // TODO: 待优化不能多次调用
    });
  }

  updateAllSeparateWallShape() {
    // 添加隔墙基础矩形轮廓
    const ids = new Set<string>();
    this.getSepDatas().forEach((separateWallData) => {
      const { startShape, endShape, baseShape } = getBaseShape(separateWallData);
      this.getSepDataMapById(separateWallData.id)?.updateExtraData(separateWallData.id, {
        drawSeparateWallInfo: { startShape, endShape, drawShape: baseShape }
      });
      ids.add(separateWallData.id);
    });
    this.computeThenUpdateWallsShape(ids);
  }

  clearAll() {
    this._baseSepWallMap.clear();
    this._separateWallMap.clear();
  }

  getDatas() {
    return {
      baseSeparateWallDatas: this.baseSeparateWallDatas,
      separateWallDatas: this.separateWallDatas
    };
  }

  coverDatas({
    baseSeparateWallDatas,
    separateWallDatas
  }: {
    baseSeparateWallDatas: SeparateWallBaseData[];
    separateWallDatas: SeparateWallBaseData[];
  }) {
    this.clearAll();

    baseSeparateWallDatas.forEach((baseData) => {
      const separateWallData = {
        ...baseData,
        drawSeparateWallInfo: initDrawSeparateWallInfo(baseData)
      };
      this.addSepBaseData(separateWallData, true);
    });
    separateWallDatas.forEach((baseData) => {
      const separateWallData = {
        ...baseData,
        drawSeparateWallInfo: initDrawSeparateWallInfo(baseData)
      };
      this.addSepBaseData(separateWallData, false);
    });

    this.updateAllSeparateWallShape();
  }
}

// 隔墙轮廓是否完全在多边形内
function isOutlineInPolygon(outline: IPoint2d[], polygon: IPoint2d[]) {
  return outline.every((point) => Curve2dUtils.isPointInPolygon(point, polygon));
}

/**
 * 隔墙轮廓是否与多边形相交（含互相包含）。
 *
 * closed 由调用方按离散前的 shape 给出，不能在这里用 outline.length 推：
 * 弧形中心线离散后点数也大于 2，当成闭合轮廓会多出一条弦，
 * 弧与弦围出的区域会被误判成墙体内部。
 */
function isOutlineIntersectPolygon(outline: IPoint2d[], polygon: IPoint2d[], closed: boolean) {
  if (outline.some((point) => Curve2dUtils.isPointInPolygon(point, polygon))) return true;

  // 中心线没有内部区域，只有成环的轮廓才需要判断多边形是否整个落在墙内
  if (closed && polygon.some((point) => Curve2dUtils.isPointInPolygon(point, outline))) return true;

  // 长墙横穿多边形时，轮廓上的点全在多边形外，只能靠边相交判断
  const polygonSegs = getPointsSegs(polygon, true);
  return getPointsSegs(outline, closed).some((outlineSeg) =>
    polygonSegs.some((polygonSeg) => outlineSeg.intersection(polygonSeg))
  );
}

function getPointsSegs(points: IPoint2d[], closed: boolean) {
  const segs: LineSeg2d[] = [];
  const segCount = closed ? points.length : points.length - 1;
  for (let i = 0; i < segCount; i++) {
    segs.push(new LineSeg2d(points[i], points[(i + 1) % points.length]));
  }

  return segs;
}

export default SeparateWallStore;
